// single-tab.ts — one live GAME tab per browser (owner 2026-09-30: "GM should not have two tabs open … same with players … into the
// FT3D or DAFT3D system"). NEWEST TAB WINS: when a campaign play page (top window with a campaign context — the FT3D / DAFT3D scene
// runs inside it) starts its websocket it announces itself on a BroadcastChannel; any OLDER game tab of the same user goes idle — its
// websocket closes and stays closed, and an overlay offers "Use here" (a reload, which re-claims and idles the other tab).
//
// Why: two live copies of the same GM page each react to every frame — two boundary watchers, two follow-ups, two optimistic
// renders racing one save. Idling is cheaper and clearer than making every lane multi-tab safe.
// A game page is a top window that hosts the FT3D / DAFT3D surface (the [data-ft3d-hud] root — my_campaign.php, item.php routed into
// it, a standalone ft3d_scene_host.php) AND has a campaign context. The context alone is not enough: every campaign admin page
// (players, settings, join requests, sessions) seeds one, and opening any of them idled the GM's live FT3D tab (2026-10-14).
// Not guarded: non-game pages (catalog, sheets, forms) and a tab that opts out with sessionStorage `ft.multiTab` = '1' (per-tab —
// the smoke harness sets it so multi-page smokes keep working; it never persists into interactive use). Same-origin iframes (the FT3D
// scene host carries its own copy of this bundle) never claim, but FOLLOW their top window: when it idles they close their own socket.
// An idle tab is fenced, not just covered: its non-GET fetches reject (Codex review 2026-09-30), so no queued work can still save.
const CHANNEL = 'ft-game-tab-v1';
const tabId = (crypto.randomUUID?.() ?? String(Math.random()).slice(2)).toUpperCase();
let claimedAt = 0;
let idle = false;
let channel: BroadcastChannel | null = null;

type Claim = { type: 'claim'; tabId: string; userId: string; at: number; reply?: boolean };

function optedOut(): boolean {
  try { return window.sessionStorage?.getItem('ft.multiTab') === '1'; } catch { return false; }
}
function userIdOf(): string { return String(window.FateTide?.user?.id ?? '').toUpperCase(); }
function isGamePage(context: unknown): boolean {
  if (window.top !== window) return false;
  if (!document.querySelector('[data-ft3d-hud]')) return false;
  const campaignId = String((context as { campaign_id?: unknown } | null)?.campaign_id ?? '');
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(campaignId);
}

/** True once a newer game tab took over: the websocket must not reconnect. */
export function gameTabIdle(): boolean { return idle; }

function showIdleOverlay(): void {
  if (document.getElementById('ft-game-tab-idle')) return;
  const wrap = document.createElement('div');
  wrap.id = 'ft-game-tab-idle';
  wrap.setAttribute('role', 'dialog');
  wrap.setAttribute('aria-modal', 'true');
  wrap.style.cssText = 'position:fixed;inset:0;z-index:2147483000;display:flex;align-items:center;justify-content:center;background:rgba(10,12,16,.82);padding:16px;';
  const box = document.createElement('div');
  box.style.cssText = 'max-width:420px;width:100%;background:#1c1f26;color:#e8e6e1;border-radius:10px;padding:22px 20px;text-align:center;font:15px/1.45 system-ui,sans-serif;box-shadow:0 10px 40px rgba(0,0,0,.5);';
  const title = document.createElement('div');
  title.textContent = 'FateTide is open in another tab';
  title.style.cssText = 'font-weight:600;font-size:17px;margin-bottom:8px;';
  const text = document.createElement('div');
  text.textContent = 'This tab stopped receiving live updates so the two do not act on the game at once.';
  text.style.cssText = 'opacity:.85;margin-bottom:16px;';
  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'btn btn-primary';
  button.textContent = 'Use here';
  button.addEventListener('click', () => { window.location.reload(); });
  box.append(title, text, button);
  wrap.append(box);
  (document.body ?? document.documentElement).append(wrap);
  button.focus();
}

/** An idle tab must not save: every non-GET fetch rejects from here on (requests already sent still settle normally). */
function fenceMutations(): void {
  const original = window.fetch.bind(window);
  window.fetch = (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const method = String(init?.method ?? (input instanceof Request ? input.method : 'GET')).toUpperCase();
    if (method !== 'GET' && method !== 'HEAD') return Promise.reject(new TypeError('FateTide is open in another tab — this tab is idle'));
    return original(input, init);
  };
}
function goIdle(closeSocket: () => void): void {
  if (idle) return;
  idle = true;
  fenceMutations();
  try { closeSocket(); } catch { /* the overlay still stops interaction */ }
  if (window.top === window) {
    (window as unknown as { __ftGameTabIdle?: boolean }).__ftGameTabIdle = true;
    const show = (): void => showIdleOverlay();
    if (document.body) show(); else document.addEventListener('DOMContentLoaded', show, { once: true });
  }
  window.dispatchEvent(new CustomEvent('ft:game-tab-idle', { detail: { tabId } }));
}

/**
 * Called by startWebSocket() before it connects. On a game page it announces this tab (idling every older game tab of the same user)
 * and listens for newer ones. `closeSocket` closes the live socket; setup.ts's reconnect checks gameTabIdle().
 */
export function claimGameTab(context: unknown, closeSocket: () => void): void {
  if (window.top !== window) { // a scene iframe follows its top window's verdict
    try {
      const top = window.top as unknown as (Window & { __ftGameTabIdle?: boolean }) | null;
      if (top?.__ftGameTabIdle) { goIdle(closeSocket); return; }
      top?.addEventListener('ft:game-tab-idle', () => goIdle(closeSocket), { once: true });
    } catch { /* cross-origin top: not a game frame */ }
    return;
  }
  if (channel || optedOut() || !isGamePage(context) || typeof BroadcastChannel !== 'function') return;
  const userId = userIdOf();
  if (!userId) return;
  channel = new BroadcastChannel(CHANNEL);
  claimedAt = Date.now();
  channel.addEventListener('message', (event: MessageEvent) => {
    const claim = event.data as Claim | null;
    if (!claim || claim.type !== 'claim' || claim.tabId === tabId || String(claim.userId).toUpperCase() !== userId) return;
    // Newest wins; a simultaneous pair breaks the tie on the tab id so exactly one of them idles.
    if (idle) return;
    if (claim.at > claimedAt || (claim.at === claimedAt && claim.tabId > tabId)) { goIdle(closeSocket); return; }
    // This tab wins against an older claim: answer once, so a newcomer that subscribed after our announcement still learns of us
    // (BroadcastChannel never replays) — it then idles itself or, if it is newer, idles us.
    if (!claim.reply) channel?.postMessage({ type: 'claim', tabId, userId, at: claimedAt, reply: true } satisfies Claim);
  });
  channel.postMessage({ type: 'claim', tabId, userId, at: claimedAt } satisfies Claim);
}
